import {
  isActivePcoImport,
  PCO_PEOPLE_STEP,
  PCO_PRODUCTS,
  pcoConnectSchema,
  pcoResolveSteps,
  pcoStepId,
  type PcoFileChoice,
  type PcoIssueSeverity,
  type PcoProduct,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { planningCenterKeys } from './keys.ts'

/**
 * Bringing a church across from Planning Center, from settings: the
 * connection, what each product holds, and the import's progress. A run is a
 * background job, so the run's query polls while it is active and stops the
 * moment it isn't.
 */

const POLL_MS = 2000

export function usePlanningCenter() {
  const api = useApi()
  return useQuery({
    queryKey: planningCenterKeys.status(),
    queryFn: async () => {
      const { data, error } = await api.api.v1['planning-center'].get()
      if (error) throw error
      return data
    },
    // While a run is going, the status card's summary follows it.
    refetchInterval: (query) =>
      query.state.data?.latest && isActivePcoImport(query.state.data.latest.status)
        ? POLL_MS
        : false,
  })
}

export type PlanningCenterStatus = NonNullable<ReturnType<typeof usePlanningCenter>['data']>
export type PcoImport = NonNullable<PlanningCenterStatus['latest']>

export function usePcoPreview(enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: planningCenterKeys.preview(),
    enabled,
    // Counting asks Planning Center a dozen questions: once a visit is plenty.
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.api.v1['planning-center'].preview.get()
      if (error) throw error
      return data
    },
  })
}

export function usePcoImport(importId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: planningCenterKeys.detail(importId ?? ''),
    enabled: importId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1['planning-center']
        .imports({ importId: importId ?? '' })
        .get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) =>
      query.state.data && isActivePcoImport(query.state.data.status) ? POLL_MS : false,
  })
}

export function usePcoImports() {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: planningCenterKeys.imports(),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1['planning-center'].imports.get({
        query: { limit: 20, ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    // A run in the list is still going: follow it until it settles.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((run) => isActivePcoImport(run.status)),
      )
        ? POLL_MS
        : false,
  })
}

export function usePcoIssues(importId: string | null, severity: PcoIssueSeverity | 'all') {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: planningCenterKeys.issues(importId ?? '', severity),
    enabled: importId !== null,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1['planning-center']
        .imports({ importId: importId ?? '' })
        .issues.get({
          query: {
            limit: 100,
            ...(severity === 'all' ? {} : { severity }),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

function useInvalidate() {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: planningCenterKeys.all() })
}

/** The connect form: the shared schema checks it before Planning Center is asked. */
export function useConnectPlanningCenter() {
  const api = useApi()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: { applicationId: string; secret: string }) => {
      const valid = pcoConnectSchema.parse(input)
      const { data, error } = await api.api.v1['planning-center'].connection.put(valid)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDisconnectPlanningCenter() {
  const api = useApi()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1['planning-center'].connection.delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
}

export function useStartPcoImport() {
  const api = useApi()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: {
      products: PcoProduct[]
      files: PcoFileChoice
      since: string | null
      skip: string[]
    }) => {
      const { data, error } = await api.api.v1['planning-center'].imports.post(input)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useCancelPcoImport() {
  const api = useApi()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (importId: string) => {
      const { data, error } = await api.api.v1['planning-center']
        .imports({ importId })
        .cancel.post()
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useResumePcoImport() {
  const api = useApi()
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (importId: string) => {
      const { data, error } = await api.api.v1['planning-center']
        .imports({ importId })
        .resume.post()
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

/**
 * The start form's choices: which products (those this version can import,
 * and that the token can read), which steps within them, which files, and
 * how far back history goes. Leaving a step out leaves out what needs it;
 * the server resolves the same way (`pcoResolveSteps`) and re-checks it all.
 */
export function usePcoImportChoices(
  status: Pick<PlanningCenterStatus, 'available' | 'catalog' | 'peopleImported'>,
  readable: ReadonlySet<PcoProduct> | null,
) {
  const choosable = PCO_PRODUCTS.filter(
    (product) => status.available.includes(product) && (readable === null || readable.has(product)),
  )
  const [unchecked, setUnchecked] = useState<ReadonlySet<PcoProduct>>(new Set())
  const [skip, setSkip] = useState<ReadonlySet<string>>(new Set())
  const [files, setFiles] = useState<PcoFileChoice>('songs_and_upcoming')
  const [since, setSince] = useState<string | null>(null)
  const chosen = choosable.filter((product) => !unchecked.has(product))
  const resolved = pcoResolveSteps(status.catalog, chosen, [...skip], status.peopleImported)
  const included = new Set(resolved.steps.map(pcoStepId))
  const waitingOnPeople = [...resolved.dropped.values()].filter(
    (need) => need === PCO_PEOPLE_STEP,
  ).length

  return {
    choosable,
    chosen,
    isChosen: (product: PcoProduct) => chosen.includes(product),
    toggle: (product: PcoProduct) =>
      setUnchecked((current) => {
        const next = new Set(current)
        if (next.has(product)) next.delete(product)
        else next.add(product)
        return next
      }),
    /** A product's steps, each with whether it will run and why not. */
    stepsOf: (product: PcoProduct) =>
      status.catalog
        .filter((step) => step.product === product)
        .map((step) => {
          const id = pcoStepId(step)
          const needs = resolved.dropped.get(id)
          return {
            ...step,
            id,
            on: included.has(id),
            /** Left out because it needs this (a label), not by choice. */
            needs: needs
              ? (status.catalog.find((s) => pcoStepId(s) === needs)?.label ?? needs)
              : null,
            /** What it comes without, because the step that adds it isn't running ("who served"). */
            without: step.fuller
              .filter((more) => !included.has(more.step))
              .map((more) => more.adds),
          }
        }),
    toggleStep: (id: string) =>
      setSkip((current) => {
        const next = new Set(current)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      }),
    stepCount: (product: PcoProduct) => ({
      on: resolved.steps.filter((step) => step.product === product).length,
      of: status.catalog.filter((step) => step.product === product).length,
    }),
    /** Steps left out because they link to people and People isn't here yet. */
    waitingOnPeople,
    /** The token can't read People at all (so including it isn't an option). */
    peopleUnreadable: readable !== null && !readable.has('people') && !status.peopleImported,
    files,
    setFiles,
    since,
    setSince,
    /** What to send: only steps of chosen products that were left out on purpose. */
    request: () => ({
      products: chosen,
      files,
      since,
      skip: [...skip].filter((id) => chosen.some((product) => id.startsWith(`${product}.`))),
    }),
  }
}
